import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_ORIGIN = 'https://star123.in';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/star123_matka_hero_background.png`;

const HOME_SEO = {
  title: 'Star123 Official | Satta Matka Chart & Live Results',
  description:
    'Open live Satta Matka charts on Star123. Track Kalyan, Sridevi, Milan and Main Bazar jodi & pana results with clear historical data.',
};

const ROBOTS =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(attr, key) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
}

function removeJsonLd(id) {
  document.getElementById(id)?.remove();
}

function canonicalPath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname
    .split('/')
    .map((segment) => {
      if (!segment) return '';
      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        return encodeURIComponent(segment);
      }
    })
    .join('/');
}

function decodeSegment(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function resolveSeo(pathname) {
  const path = pathname || '/';

  if (path === '/') return { ...HOME_SEO, path: '/' };

  if (path === '/charts') {
    return {
      title: 'Satta Matka Chart | Star123 Jodi & Pana',
      description:
        'Browse Star123 market charts for Kalyan, Sridevi, Milan and Main Bazar. View jodi and pana history in one place.',
      path,
    };
  }

  if (path === '/chart/starline') {
    return {
      title: 'Starline Charts | Star123 Satta Matka',
      description:
        'Check Star123 Starline charts and historical pana records. Track Starline market results with clear, updated chart data.',
      path,
    };
  }

  if (path === '/chart/jackpot') {
    return {
      title: 'Jackpot Charts | Star123 Satta Matka',
      description:
        'Check Star123 Jackpot charts and historical pana records. Track Jackpot market results with clear, updated chart data.',
      path,
    };
  }

  if (path === '/privacy-policy') {
    return {
      title: 'Privacy Policy | Star123',
      description:
        'Read the Star123 Privacy Policy to understand how we use and protect information when you visit our Satta Matka charts platform.',
      path,
    };
  }

  if (path === '/how-to-play') {
    return {
      title: 'How to Play Satta Matka | Star123',
      description:
        'Learn how to play Satta Matka on Star123, including number selection, deposits, and gameplay steps explained in simple language.',
      path,
    };
  }

  if (path === '/game-rules') {
    return {
      title: 'Game Rules | Star123',
      description:
        'Read Star123 game rules covering withdrawals, unfair bets, and fair play guidelines for the Satta Matka platform.',
      path,
    };
  }

  const jodi = path.match(/^\/charts\/jodi\/(.+)$/);
  if (jodi) {
    const name = decodeSegment(jodi[1]);
    return {
      title: `${name} Jodi Chart | Star123`,
      description: `View the ${name} jodi chart on Star123 with historical Satta Matka records, past results and clear bracket data.`,
      path,
      breadcrumbName: `${name} Jodi Chart`,
    };
  }

  const pana = path.match(/^\/charts\/pana\/(.+)$/);
  if (pana) {
    const name = decodeSegment(pana[1]);
    return {
      title: `${name} Pana Chart | Star123`,
      description: `View the ${name} pana chart on Star123 with historical Satta Matka panel records and past results.`,
      path,
      breadcrumbName: `${name} Pana Chart`,
    };
  }

  const starline = path.match(/^\/chart\/starline\/(.+)$/);
  if (starline) {
    const name = decodeSegment(starline[1]);
    return {
      title: `Starline ${name} Pana Chart | Star123`,
      description: `View the Starline ${name} pana chart on Star123 with historical Satta Matka records and results.`,
      path,
      breadcrumbName: `Starline ${name}`,
    };
  }

  const jackpot = path.match(/^\/chart\/jackpot\/(.+)$/);
  if (jackpot) {
    const name = decodeSegment(jackpot[1]);
    return {
      title: `Jackpot ${name} Pana Chart | Star123`,
      description: `View the Jackpot ${name} pana chart on Star123 with historical Satta Matka records and results.`,
      path,
      breadcrumbName: `Jackpot ${name}`,
    };
  }

  return { ...HOME_SEO, path };
}

function buildBreadcrumb(seo, url) {
  if (!seo.breadcrumbName) return null;

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_ORIGIN}/`,
    },
  ];

  if (seo.path.startsWith('/charts/')) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Market Charts',
      item: `${SITE_ORIGIN}/charts`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: seo.breadcrumbName,
      item: url,
    });
  } else if (seo.path.startsWith('/chart/starline')) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Starline Charts',
      item: `${SITE_ORIGIN}/chart/starline`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: seo.breadcrumbName,
      item: url,
    });
  } else if (seo.path.startsWith('/chart/jackpot')) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Jackpot Charts',
      item: `${SITE_ORIGIN}/chart/jackpot`,
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: seo.breadcrumbName,
      item: url,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: items,
  };
}

function PageSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const current = resolveSeo(pathname);
    const url = `${SITE_ORIGIN}${canonicalPath(current.path)}`;
    document.title = current.title;
    upsertMeta('name', 'description', current.description);
    removeMeta('name', 'keywords');
    upsertMeta('name', 'robots', ROBOTS);
    upsertMeta('name', 'googlebot', ROBOTS);
    upsertMeta('property', 'og:title', current.title);
    upsertMeta('property', 'og:description', current.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', DEFAULT_IMAGE);
    upsertMeta('property', 'og:image:alt', 'Star123 Satta Matka official website');
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Star123');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', current.title);
    upsertMeta('name', 'twitter:description', current.description);
    upsertMeta('name', 'twitter:image', DEFAULT_IMAGE);
    upsertCanonical(url);

    const breadcrumb = buildBreadcrumb(current, url);
    if (breadcrumb) {
      upsertJsonLd('star123-breadcrumb-schema', breadcrumb);
    } else {
      removeJsonLd('star123-breadcrumb-schema');
    }
  }, [pathname]);

  return null;
}

export default PageSeo;
